import * as React from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyValue, StatusPill } from "@ozpanel/kit";
import { Button, Panel, Section, StateView, When, useAuth, useToast } from "@ozpanel/ui";

/**
 * The cluster's time: per node, what disciplines its clock, whether that is verified, and its
 * measured offset to every other node — /clock/cluster, which reads each node's OWN row (every node
 * writes only its own, once a measurement). Nothing here is asked of a node per page load.
 *
 * Why it sits on the cluster screen: every staleness limit, lease and takeover check on this screen
 * compares a time one node wrote with another node's clock. A node a second out reads as a lease
 * stolen early or a live node gone silent — never as a clock — unless this panel says so first.
 *
 * Three states, never two. A node that has not reported, whose row is stale, or whose state is
 * "unknown" is UNMEASURED: it is drawn as such and counted, and is never shown as synchronised. An
 * unmeasured PEER has no offset (null), which is not an offset of zero.
 *
 * Field names are the Go JSON tags of internal/modules/clock (Snapshot, Local, PeerOffset, Offset,
 * NodeView, ClusterView); the module's own test fails if a served key is not a declared one.
 */

interface Offset {
  node: string;
  offset_ms: number;
  uncertainty_ms: number;
  round_trip_ms: number;
  excess_ms: number;
}
interface PeerOffset {
  node: string;
  offset: Offset | null;
  via?: string;
  samples?: number;
  error?: string;
}
interface Local {
  container: string;
  owns_clock: boolean | null;
  client: string;
  kernel_synchronized: boolean | null;
  source: string;
  source_offset_ms: number | null;
  stratum: number | null;
  last_sync_at: string | null;
  servers: string;
  unread?: Record<string, string>;
}
interface Snapshot {
  node: string;
  measured_at: string;
  interval_sec: number;
  state: "synchronized" | "unsynchronized" | "unknown";
  basis: string;
  local: Local | null;
  local_error?: string;
  peers: PeerOffset[] | null;
  peers_expected: number;
  peers_measured: number;
  peers_error?: string;
  max_offset: Offset | null;
  unmeasured_for_sec: number | null;
  reference?: Reference | null;
}
/** One public time server as one node measured it (clock/reference.go RefServer). */
interface RefServer {
  server: string;
  offset: Offset | null;
  address?: string;
  stratum?: number;
  samples?: number;
  error?: string;
}
interface Reference {
  servers: RefServer[] | null;
  measured_at: string;
  max_offset: Offset | null;
  configured: boolean;
  error?: string;
}
interface NodeView {
  node: string;
  self: boolean;
  reported: boolean;
  fresh: boolean;
  age_sec: number | null;
  age_corrected: boolean;
  stale_after_sec: number;
  snapshot: Snapshot | null;
}
interface Thresholds {
  offset_warn_ms: number;
  offset_error_ms: number;
  unmeasured_after_sec: number;
}
interface ClusterView {
  nodes: NodeView[] | null;
  max_offset: (Offset & { from: string; level: "ok" | "warn" | "error" }) | null;
  unmeasured: number;
  thresholds: Thresholds;
  note: string;
  at: string;
}

const muted = (s: string) => <span className="text-muted">{s}</span>;

function fmtMs(v: number): string {
  const a = Math.abs(v);
  const digits = a >= 100 ? 0 : a >= 10 ? 1 : a >= 1 ? 2 : 3;
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)} ms`;
}

function age(sec: number | null): string {
  if (sec === null) return "never";
  if (sec < 0) return "in the future";
  if (sec < 90) return `${sec}s ago`;
  if (sec < 5400) return `${Math.round(sec / 60)} min ago`;
  return `${Math.round(sec / 3600)} h ago`;
}

/** The level of one offset, by the server's thresholds — the console carries no limits of its own. */
function levelOf(o: Offset, th: Thresholds): "ok" | "warn" | "error" {
  if (o.excess_ms >= th.offset_error_ms) return "error";
  if (o.excess_ms >= th.offset_warn_ms) return "warn";
  return "ok";
}
const levelTone = { ok: "ok", warn: "warn", error: "bad" } as const;

function OffsetCell({ p, th }: { p: PeerOffset; th: Thresholds }) {
  if (!p.offset) {
    // Unmeasured is its own thing: the peer could not be asked. Never rendered as 0.
    return (
      <span className="text-warn" title={p.error}>
        unmeasured
      </span>
    );
  }
  const lv = levelOf(p.offset, th);
  return (
    <span className="flex items-center justify-end gap-2">
      <span className={`font-mono ${lv === "error" ? "text-crit" : lv === "warn" ? "text-warn" : ""}`}>
        {fmtMs(p.offset.offset_ms)} <span className="text-muted">± {p.offset.uncertainty_ms.toFixed(p.offset.uncertainty_ms >= 10 ? 1 : 2)}</span>
      </span>
      {lv !== "ok" ? <StatusPill tone={levelTone[lv]}>{lv}</StatusPill> : null}
    </span>
  );
}

/** A reference server's offset cell: unmeasured is said, never 0. */
function RefCell({ r, th }: { r: RefServer; th: Thresholds }) {
  return <OffsetCell p={{ node: r.server, offset: r.offset, error: r.error }} th={th} />;
}

function NodeClockCard({ n, th }: { n: NodeView; th: Thresholds }) {
  const crit = "border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)]";
  const s = n.snapshot;
  if (!n.reported || !s) {
    return (
      <Panel className={`overflow-hidden ${crit}`}>
        <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
          <span className="font-mono text-[13px]">{n.node}</span>
          <StatusPill tone="bad">UNMEASURED</StatusPill>
        </div>
        <div className="px-3 py-2 text-[12px] leading-relaxed text-crit">
          This node has never reported a clock measurement — its clock is unknown, not synchronised.
        </div>
      </Panel>
    );
  }
  const l = s.local;
  const bad = !n.fresh || s.state !== "synchronized";
  const peers = s.peers ?? [];
  return (
    <Panel className={`overflow-hidden ${bad ? crit : ""}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
        <span className="font-mono text-[13px]">
          {n.node}
          {n.self ? <span className="ml-1.5 text-[11px] text-muted">this node</span> : null}
        </span>
        <span className="flex flex-wrap gap-1.5">
          {l && l.owns_clock === false ? <StatusPill tone="neutral">container · host's clock</StatusPill> : null}
          {!n.fresh ? (
            <StatusPill tone="bad">STALE</StatusPill>
          ) : s.state === "synchronized" ? (
            <StatusPill tone="ok">synchronised</StatusPill>
          ) : s.state === "unsynchronized" ? (
            <StatusPill tone="bad">NOT synchronised</StatusPill>
          ) : (
            <StatusPill tone="warn">unknown</StatusPill>
          )}
        </span>
      </div>
      {!n.fresh && (
        <div className="border-b border-line px-3 py-2 text-[12px] leading-relaxed text-crit">
          Last measured {age(n.age_sec)} (stale after {Math.round(n.stale_after_sec / 60)} min): this node has stopped
          measuring, so what follows is what its clock WAS, not what it is.
        </div>
      )}
      <div className="px-3 py-2 text-[12px] leading-relaxed text-muted">{s.basis}</div>
      <KeyValue
        dense
        rows={[
          {
            key: "client",
            label: "Time-sync client",
            value: l?.client ?? "",
            render: () => (l ? (l.client ? <span className="font-mono">{l.client}</span> : muted("could not be asked")) : muted(s.local_error || "not read")),
          },
          {
            key: "source",
            label: "Source",
            value: l?.source ?? "",
            render: () =>
              l?.source ? (
                <span className="font-mono">
                  {l.source}
                  {l.source_offset_ms !== null ? <span className="text-muted"> · {fmtMs(l.source_offset_ms)}</span> : null}
                </span>
              ) : l && l.owns_clock === false ? (
                muted("its host's — cannot be asked from a container")
              ) : (
                muted("none")
              ),
          },
          {
            key: "kernel",
            label: l && l.owns_clock === false ? "Host kernel says" : "Kernel says",
            value: l?.kernel_synchronized ?? null,
            render: () =>
              !l || l.kernel_synchronized === null ? (
                muted("could not be read")
              ) : l.kernel_synchronized ? (
                <span>synchronised</span>
              ) : (
                <span className="text-crit">NOT synchronised</span>
              ),
          },
          ...peers.map((p) => ({
            key: `peer-${p.node}`,
            label: `Offset to ${p.node}`,
            value: p.offset?.offset_ms ?? null,
            hint: p.offset ? `round trip ${p.offset.round_trip_ms.toFixed(2)} ms${p.via ? ` via ${p.via}` : ""}` : p.error,
            render: () => <OffsetCell p={p} th={th} />,
          })),
          ...(s.reference && s.reference.configured
            ? (s.reference.servers ?? []).map((r) => ({
                key: `ref-${r.server}`,
                label: `Offset to ${r.server}`,
                value: r.offset?.offset_ms ?? null,
                hint: r.offset
                  ? `reference server · round trip ${r.offset.round_trip_ms.toFixed(2)} ms${r.address ? ` via ${r.address}` : ""}${r.stratum ? ` · stratum ${r.stratum}` : ""}`
                  : r.error,
                render: () => <RefCell r={r} th={th} />,
              }))
            : [
                {
                  key: "ref-none",
                  label: "Reference servers",
                  value: null,
                  render: () => muted(s.reference?.error || (s.reference ? "none configured" : "not measured by this node's build")),
                },
              ]),
          {
            key: "measured",
            label: "Measured",
            value: n.age_sec,
            hint: n.age_corrected ? "age corrected by this node's measured offset to it" : undefined,
            render: () => <span>{age(n.age_sec)}</span>,
          },
        ]}
      />
      {s.peers_error ? <div className="border-t border-line px-3 py-2 text-[12px] text-warn">{s.peers_error}</div> : null}
    </Panel>
  );
}

/**
 * The reference time servers: the cluster setting reference_servers of the clock module, edited here
 * through the module's own settings write (PUT /clock/settings — one writer), and tried one at a time
 * with POST /clock/reference/test (measures from THIS node, saves nothing).
 */
function ReferenceServers() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["clock-settings"],
    queryFn: () =>
      client.apiFetch<{
        values?: Record<string, unknown>;
        cluster?: { desired?: Record<string, { value: string; source: string }> };
      }>("/clock/settings"),
    retry: false,
  });
  const desired = q.data?.cluster?.desired?.reference_servers;
  const list = (desired?.value ?? "").split(/\s+/).filter(Boolean);
  const [adding, setAdding] = React.useState("");
  const [tests, setTests] = React.useState<Record<string, RefServer & { node?: string }>>({});
  const save = useMutation({
    mutationFn: (next: string[]) =>
      client.apiFetch<unknown>("/clock/settings", { method: "PUT", body: JSON.stringify({ reference_servers: next.join(" ") }) }),
    onSuccess: () => {
      setAdding("");
      qc.invalidateQueries({ queryKey: ["clock-settings"] });
      qc.invalidateQueries({ queryKey: ["clock-cluster"] });
      notify({ kind: "success", title: "Reference servers saved", body: "Every node measures against them within five minutes." });
    },
    onError: (e) => notify({ kind: "error", title: "Not saved", body: e instanceof Error ? e.message : String(e) }),
  });
  const test = useMutation({
    mutationFn: (server: string) =>
      client.apiFetch<RefServer & { node?: string }>("/clock/reference/test", { method: "POST", body: JSON.stringify({ server }) }),
    onSuccess: (r) => setTests((t) => ({ ...t, [r.server]: r })),
    onError: (e, server) => setTests((t) => ({ ...t, [server]: { server, offset: null, error: e instanceof Error ? e.message : String(e) } })),
  });
  const result = (server: string) => {
    const r = tests[server];
    if (!r) return null;
    return r.offset ? (
      <span className="font-mono text-[12px]">
        {fmtMs(r.offset.offset_ms)} ± {r.offset.uncertainty_ms.toFixed(2)} ms{r.stratum ? ` · stratum ${r.stratum}` : ""}
        {r.node ? <span className="text-muted"> · from {r.node}</span> : null}
      </span>
    ) : (
      <span className="text-[12px] text-warn">unmeasured — {r.error}</span>
    );
  };
  return (
    <div className="rounded border border-line px-3 py-2 text-[12.5px] leading-relaxed">
      <b>Reference time servers</b>
      <span className="text-muted">
        {" "}
        — each node measures its OWN offset against these (SNTP, every five minutes), so three nodes that agree cannot hide
        all three being wrong. Measurement only: nothing is disciplined by them.
        {desired ? ` (${desired.source})` : ""}
      </span>
      <StateView loading={q.isLoading} error={q.error} errorContext="the clock settings (/clock/settings)">
        <div className="mt-2 space-y-1.5">
          {list.length === 0 ? <div className="text-warn">None configured — no node is compared with the world.</div> : null}
          {list.map((srv) => (
            <div key={srv} className="flex flex-wrap items-center gap-2">
              <span className="w-56 font-mono">{srv}</span>
              <Button size="sm" variant="ghost" disabled={test.isPending} onClick={() => test.mutate(srv)}>
                Test now
              </Button>
              <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => save.mutate(list.filter((x) => x !== srv))}>
                Remove
              </Button>
              {result(srv)}
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <input
              className="w-56 rounded border border-line bg-surface px-2 py-1 font-mono text-[13px]"
              placeholder="time.example.net"
              value={adding}
              onChange={(e) => setAdding(e.target.value.trim())}
            />
            <Button size="sm" variant="ghost" disabled={!adding || test.isPending} onClick={() => test.mutate(adding)}>
              Test
            </Button>
            <Button size="sm" disabled={!adding || list.includes(adding) || save.isPending} onClick={() => save.mutate([...list, adding])}>
              Add
            </Button>
            {adding ? result(adding) : null}
          </div>
        </div>
      </StateView>
    </div>
  );
}

export function ClusterTimePanel() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["clock-cluster"],
    queryFn: () => client.apiFetch<ClusterView>("/clock/cluster"),
    refetchInterval: 30000,
    retry: false,
  });
  const d = q.data;
  const nodes = d?.nodes ?? [];
  const unsynced = nodes.filter((n) => n.reported && n.fresh && n.snapshot?.state === "unsynchronized").length;
  const max = d?.max_offset ?? null;

  const badge = !d ? undefined : unsynced > 0 ? (
    <StatusPill tone="bad">{unsynced} not synchronised</StatusPill>
  ) : max && max.level !== "ok" ? (
    <StatusPill tone={levelTone[max.level]}>offset {max.level}</StatusPill>
  ) : d.unmeasured > 0 || !max ? (
    <StatusPill tone="warn">{d.unmeasured > 0 ? `${d.unmeasured} unmeasured` : "no offset measured"}</StatusPill>
  ) : (
    <StatusPill tone="ok">clocks agree</StatusPill>
  );

  return (
    <Section
      title="Cluster time"
      hint="each node's own measurement of its clock and its offset to every peer"
      actions={
        <span className="flex items-center gap-2">
          {badge}
          <Button size="sm" variant="ghost" asChild>
            <Link to="/modules/clock">NTP servers…</Link>
          </Button>
        </span>
      }
      bodyClassName="p-0"
    >
      <div className="space-y-3 p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="the cluster's time measurements (/clock/cluster)">
          {d && (
            <>
              <div className="rounded border border-line px-3 py-2 text-[12.5px] leading-relaxed">
                <b>Largest offset in the cluster: </b>
                {max ? (
                  <span className={max.level === "error" ? "text-crit" : max.level === "warn" ? "text-warn" : ""}>
                    <span className="font-mono">
                      {fmtMs(max.offset_ms)} ± {max.uncertainty_ms.toFixed(2)} ms
                    </span>{" "}
                    — {max.from} measured against {max.node}
                  </span>
                ) : (
                  <span className="text-warn">not known — no node holds a fresh measurement of a peer</span>
                )}
                <span className="text-muted">
                  {" "}
                  · warns beyond {d.thresholds.offset_warn_ms} ms, error beyond {d.thresholds.offset_error_ms} ms, each
                  counted past the measurement's own uncertainty
                </span>
              </div>
              <ReferenceServers />
              {nodes.length === 0 ? (
                <div className="text-[12.5px] text-crit">The route answered with no nodes at all — nothing was measured.</div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {nodes.map((n) => (
                    <NodeClockCard key={n.node} n={n} th={d.thresholds} />
                  ))}
                </div>
              )}
              <div className="text-[12px] leading-relaxed text-muted">
                {d.note}
                {d.at ? (
                  <>
                    {" "}
                    · read <When value={d.at} />
                  </>
                ) : null}
              </div>
            </>
          )}
        </StateView>
      </div>
    </Section>
  );
}
